import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  TextField,
  Checkbox,
  Avatar,
  Menu,
  MenuItem,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  CloseCircle,
  ShoppingCart,
  ArrowLeft2,
  ArrowDown2,
  Gallery,
  Trash,
  TickCircle,
  SearchNormal1,
  Edit2,
  Refresh,
} from "iconsax-react";
import { fetchCatalogs, fetchCatalogsFromDb, fetchCatalogSets, createCatalogSet, updateCatalogSet, deleteCatalogSet } from "../../../../redux/Slice/Catalog/catalogSlice";
import toast from "react-hot-toast";

/** Meta/WhatsApp API allows at most 30 products in a single catalog message. */
const MAX_CATALOG_PRODUCTS = 30;

/** Stable identity for a product across both live catalog products and saved-set snapshots. */
const productKey = (p) => p.retailerId || p.retailer_id || p.product_retailer_id || p.id || "";

const hasValidImage = (url) =>
  !!url && url !== "null" && url !== "undefined" && (url.startsWith("http") || url.startsWith("data:"));

/**
 * InboxCatalogSend
 * Composer dialog for picking catalog products (or a saved "Catalog Set") to
 * send from the inbox. Same dialog scaffold as TemplateSend.jsx (fullscreen on
 * mobile, rounded lg desktop sheet, header + close), so this reads as part of
 * the same family as Templates/Actions rather than a one-off design.
 *
 * onProductsSelected receives either { catalogId, catalogName, products } — the
 * caller (TextBox.jsx) stages this in the composer for review before it's
 * actually sent, same as picked files — or { fullCatalogLink: true } from the
 * "Send Full Catalog" button, which TextBox.jsx resolves to the workspace's
 * wa.me/c/<phoneNumber> link instead of a product selection.
 */
const InboxCatalogSend = ({ open, onClose, onProductsSelected, platform }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { catalogs, status, totalCatalogs, sets, setsStatus } = useSelector((state) => state.catalog);

  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [catalogMenuAnchor, setCatalogMenuAnchor] = useState(null);
  const [mode, setMode] = useState("products"); // 'products' | 'sets'
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [savingSet, setSavingSet] = useState(false);
  const [newSetName, setNewSetName] = useState("");
  // The Catalog Set currently being edited (products + name shown in the products tab for
  // review), or null when just picking products fresh / not editing anything.
  const [editingSet, setEditingSet] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  // Which product cards have their description expanded ("See More") — keyed
  // by productKey, reset whenever the dialog reopens or the catalog changes.
  const [expandedDescriptions, setExpandedDescriptions] = useState(() => new Set());

  // Load from the DB cache on every open (cheap — no Meta API call) rather than re-fetching
  // live from Meta each time. Catalogs/products can still change on Meta's side between one
  // open and the next, so this can go stale; the Refresh button below (handleRefresh) does the
  // live Meta fetch and updates that cache for next time.
  useEffect(() => {
    if (open) {
      dispatch(fetchCatalogsFromDb(platform));
    }
  }, [open, dispatch, platform]);

  // Default to the first catalog once the (freshly-fetched) list loads.
  useEffect(() => {
    if (open && !selectedCatalog && catalogs.length > 0) {
      setSelectedCatalog(catalogs[0]);
    }
  }, [open, catalogs, selectedCatalog]);

  // Reset transient state whenever the dialog is reopened — selectedCatalog included, so a
  // stale selection from a previous open can't survive into this one while the fresh fetch
  // above is still in flight.
  useEffect(() => {
    if (open) {
      setSelectedCatalog(null);
      setSelectedProducts([]);
      setMode("products");
      setSavingSet(false);
      setNewSetName("");
      setEditingSet(null);
      setProductSearch("");
      setExpandedDescriptions(new Set());
    }
  }, [open]);

  // Fetch saved sets for the active catalog whenever the Catalog Sets tab is opened.
  useEffect(() => {
    if (open && mode === "sets" && selectedCatalog?.id) {
      dispatch(fetchCatalogSets(selectedCatalog.id));
    }
  }, [open, mode, selectedCatalog?.id, dispatch]);

  const products = useMemo(() => selectedCatalog?.products || [], [selectedCatalog]);

  const filteredProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(term));
  }, [products, productSearch]);

  const toggleDescription = useCallback((key) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleToggleProduct = useCallback((product) => {
    setSelectedProducts((prev) => {
      const key = productKey(product);
      const isSelected = prev.some((p) => productKey(p) === key);
      if (isSelected) return prev.filter((p) => productKey(p) !== key);
      if (prev.length >= MAX_CATALOG_PRODUCTS) return prev;
      return [...prev, product];
    });
  }, []);

  // Clicking a set sends it as-is straight to the composer preview — that's the whole point of
  // a saved set (skip re-picking products every time) — rather than dropping into the products
  // tab for another confirm step. Built directly from `set`, not selectedCatalog/selectedProducts
  // state, since neither needs to be populated first. onProductsSelected (TextBox.jsx) both
  // stages the products in the composer and closes this dialog.
  const handleUseSet = (set) => {
    const products = (set.products || []).slice(0, MAX_CATALOG_PRODUCTS);
    if (products.length === 0) return;
    onProductsSelected({
      catalogId: set.catalogId,
      catalogName: set.catalogName,
      products,
    });
  };

  // "Full Catalog" no longer sends an interactive multi-product message (Meta caps those at
  // 30 items) — it sends the wa.me/c/<phoneNumber> deep link, which opens the WHOLE connected
  // catalog on WhatsApp. Not tied to a specific product selection, so this just tells the
  // composer (TextBox.jsx) to resolve and stage that link, same immediate-to-composer
  // behavior as clicking a saved Catalog Set.
  const handleSendFullCatalog = () => {
    onProductsSelected({ fullCatalogLink: true });
  };

  // Refetches live from Meta and updates the DB cache (same endpoint CatalogSend.jsx's Refresh
  // button in the workflow drawers uses) — the explicit path back to fresh data, since the
  // effect above now only reads the cache.
  const handleRefresh = () => {
    dispatch(fetchCatalogs(platform));
  };

  const handleDeleteSet = (e, setId) => {
    e.stopPropagation();
    dispatch(deleteCatalogSet(setId));
  };

  const handleEditSet = (e, set) => {
    e.stopPropagation();
    setSelectedCatalog((prev) => (prev?.id === set.catalogId ? prev : { id: set.catalogId, name: set.catalogName }));
    setSelectedProducts(set.products || []);
    setNewSetName(set.name);
    setEditingSet(set);
    setMode("products");
  };

  const handleCancelEditSet = () => {
    setEditingSet(null);
    setSelectedProducts([]);
    setNewSetName("");
  };

  const handleUpdateSet = async () => {
    const name = newSetName.trim();
    if (!editingSet || !name || selectedProducts.length === 0 || !selectedCatalog) return;
    try {
      await dispatch(
        updateCatalogSet({
          id: editingSet._id || editingSet.id,
          name,
          catalogId: selectedCatalog.id,
          catalogName: selectedCatalog.name,
          products: selectedProducts,
        })
      ).unwrap();
      toast.success(`Updated "${name}"`);
      setEditingSet(null);
      setSelectedProducts([]);
      setNewSetName("");
      setMode("sets");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to update catalog set");
    }
  };

  const handleSaveAsSet = async () => {
    const name = newSetName.trim();
    if (!name || selectedProducts.length === 0 || !selectedCatalog) return;
    try {
      await dispatch(
        createCatalogSet({
          name,
          catalogId: selectedCatalog.id,
          catalogName: selectedCatalog.name,
          products: selectedProducts,
        })
      ).unwrap();
      toast.success(`Saved "${name}" as a Catalog Set`);
      setSavingSet(false);
      setNewSetName("");
      // Jump to Catalog Sets so the set just created is right there, and clear the product
      // selection it was built from — otherwise those products stayed checked on the products
      // tab, reading as still "in progress" even though the set was already saved.
      setSelectedProducts([]);
      setMode("sets");
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to save catalog set");
    }
  };

  const handleConfirmSend = () => {
    if (selectedProducts.length === 0 || !selectedCatalog) return;
    onProductsSelected({
      catalogId: selectedCatalog.id,
      catalogName: selectedCatalog.name,
      products: selectedProducts.slice(0, MAX_CATALOG_PRODUCTS),
    });
  };

  const catalogSetsForActive = useMemo(
    () => (selectedCatalog ? sets.filter((s) => s.catalogId === selectedCatalog.id) : sets),
    [sets, selectedCatalog]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isSmDown}
      fullWidth
      maxWidth="md"
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          padding: 0,
          margin: isSmDown ? 0 : undefined,
          maxHeight: isSmDown ? "100%" : "min(88dvh, 760px)",
          height: isSmDown ? "100%" : "min(88dvh, 680px)",
          overflow: "hidden",
          borderRadius: isSmDown ? 0 : 3,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogContent sx={{ display: "flex", flexDirection: "column", p: 0, overflow: "hidden", flex: 1, minHeight: 0 }}>
        {/* Header — same "1rem/600 heading + close X" treatment as TemplateSend.jsx. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1.25, sm: "20px" },
            pt: { xs: 0.75, sm: 1.25 },
            pb: { xs: 0.75, sm: 1 },
          }}
        >
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
            Send Catalog
          </Typography>
          <IconButton size="small" aria-label="Close dialog" onClick={onClose}>
            <CloseCircle size={isSmDown ? 18 : 20} color="currentColor" variant="Linear" />
          </IconButton>
        </Box>
        <Divider />

        {/* Sub-header: Catalog Sets toggle + Full Catalog action (left, same button style) /
            catalog picker (right, only when there's a choice). owned_product_catalogs lists
            every catalog the Business Manager owns, not specifically the one connected to this
            WhatsApp Business Account — Meta only ever lets ONE of them actually send
            successfully. There's no field on this response that says which one that is, and
            defaulting silently to whichever comes back first isn't safe (it may not be the
            connected one at all) — so this exposes all of them and lets the agent try/switch
            instead of silently locking onto a possibly-broken default.
            Hidden entirely while editing a set — none of Catalog Sets / Send Full Catalog /
            switching catalogs make sense mid-edit, and the footer's own Cancel is the only
            way out of that state, kept deliberately narrow. */}
        {!editingSet && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1.25, sm: "20px" },
            py: 1,
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {mode === "sets" ? (
              // Inside Catalog Sets: a back arrow to leave it (Send Full Catalog only makes
              // sense on the products tab, so it's not shown here at all) + a plain label
              // instead of the entry button, since we're already inside that tab.
              <>
                <IconButton size="small" onClick={() => setMode("products")} aria-label="Back to products">
                  <ArrowLeft2 size={18} color="currentColor" variant="Linear" />
                </IconButton>
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary" }}>
                  Catalog Sets
                </Typography>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setMode("sets")}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    borderRadius: "8px",
                    px: 1.25,
                  }}
                >
                  Catalog Sets
                </Button>
                {/* Sends the wa.me/c/<phoneNumber> link to the whole connected catalog —
                    an immediate send-to-composer, same as clicking a saved Catalog Set. */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSendFullCatalog}
                  disabled={!selectedCatalog}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    borderRadius: "8px",
                    px: 1.25,
                  }}
                >
                  Send Full Catalog
                </Button>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {catalogs.length > 1 ? (
              <>
                <Button
                  size="small"
                  onClick={(e) => setCatalogMenuAnchor(e.currentTarget)}
                  endIcon={<ArrowDown2 size={14} color="currentColor" variant="Linear" />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: "text.primary",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "8px",
                    px: 1.25,
                    maxWidth: 220,
                  }}
                >
                  <Typography component="span" noWrap sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
                    {selectedCatalog?.name || "Select Catalog"}
                  </Typography>
                </Button>
                <Menu anchorEl={catalogMenuAnchor} open={!!catalogMenuAnchor} onClose={() => setCatalogMenuAnchor(null)}>
                  {catalogs.map((catalog) => (
                    <MenuItem
                      key={catalog.id}
                      selected={selectedCatalog?.id === catalog.id}
                      onClick={() => {
                        setSelectedCatalog(catalog);
                        setSelectedProducts([]);
                        setCatalogMenuAnchor(null);
                      }}
                    >
                      {catalog.name}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              selectedCatalog?.name && (
                <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.secondary", maxWidth: 220 }}>
                  {selectedCatalog.name}
                </Typography>
              )
            )}
            {/* Products shown here come from the DB cache (see the fetchCatalogsFromDb effect
                above) — this re-fetches live from Meta and updates that cache. */}
            <IconButton
              size="small"
              aria-label="Refresh catalogs"
              onClick={handleRefresh}
              disabled={status === "loading"}
            >
              <Refresh size={16} color="currentColor" variant="Linear" />
            </IconButton>
          </Box>
        </Box>
        )}

        <Divider />

        {/* Main tab — product list or saved sets. */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 1.25, sm: "20px" }, py: 1.5 }}>
          {status === "loading" && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {status !== "loading" && totalCatalogs === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <ShoppingCart size={40} color={theme.palette.text.secondary} variant="Linear" style={{ opacity: 0.5 }} />
              <Typography sx={{ mt: 1.5, fontSize: "0.875rem", color: "text.secondary" }}>
                No product catalogs found for this workspace.
              </Typography>
            </Box>
          )}

          {status !== "loading" && selectedCatalog && mode === "products" && (
            <>
              {editingSet && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    p: 1,
                    borderRadius: "8px",
                    backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08),
                  }}
                >
                  <Edit2 size={16} color={theme.palette.primary.main} variant="Linear" style={{ flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "0.8125rem", color: "primary.main", flex: 1 }}>
                    Editing "{editingSet.name}" — adjust products below, then Update Set.
                  </Typography>
                  <Button size="small" onClick={handleCancelEditSet} sx={{ textTransform: "none", fontSize: "0.75rem", flexShrink: 0 }}>
                    Cancel
                  </Button>
                </Box>
              )}

              {/* Search only — "Full Catalog" moved up to the sub-header next to Catalog Sets
                  (see handleSendFullCatalog); it still just pre-checks everything here for
                  review, same as before, so nothing needs to live in this toolbar for it. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <TextField
                  size="small"
                  placeholder="Search for products..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchNormal1 size={16} color={theme.palette.text.secondary} variant="Linear" style={{ marginRight: 6, flexShrink: 0 }} />,
                  }}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8125rem" } }}
                />
              </Box>

              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 1 }}>
                {selectedProducts.length}/{MAX_CATALOG_PRODUCTS} selected — select up to {MAX_CATALOG_PRODUCTS} products to share.
              </Typography>

              {filteredProducts.length === 0 ? (
                <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", fontStyle: "italic", py: 2 }}>
                  {products.length === 0 ? "No products in this catalog." : "No products match your search."}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 1.25,
                  }}
                >
                  {filteredProducts.map((product) => {
                    const key = productKey(product);
                    const isSelected = selectedProducts.some((p) => productKey(p) === key);
                    const atLimit = !isSelected && selectedProducts.length >= MAX_CATALOG_PRODUCTS;
                    const isExpanded = expandedDescriptions.has(key);
                    return (
                      <Box
                        key={key}
                        onClick={() => !atLimit && handleToggleProduct(product)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                          borderRadius: "10px",
                          overflow: "hidden",
                          cursor: atLimit ? "not-allowed" : "pointer",
                          opacity: atLimit ? 0.5 : 1,
                          backgroundColor: "background.paper",
                          "&:hover": { borderColor: atLimit ? theme.palette.divider : theme.palette.primary.main },
                        }}
                      >
                        {/* Image + checkbox overlay */}
                        <Box sx={{ position: "relative", width: "100%", aspectRatio: "1 / 1", backgroundColor: "action.hover" }}>
                          {hasValidImage(product.imageUrl) ? (
                            <Box
                              component="img"
                              src={product.imageUrl}
                              alt={product.name}
                              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                          ) : (
                            <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Gallery size={28} color={theme.palette.text.secondary} variant="Linear" />
                            </Box>
                          )}
                          <Checkbox
                            checked={isSelected}
                            disabled={atLimit}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleToggleProduct(product)}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              left: 4,
                              p: 0.375,
                              backgroundColor: alpha(theme.palette.background.paper, 0.9),
                              borderRadius: "6px",
                              "&:hover": { backgroundColor: theme.palette.background.paper },
                            }}
                          />
                        </Box>

                        {/* Name / description / price */}
                        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.25, flex: 1 }}>
                          <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary" }}>
                            {product.name || "Unnamed Product"}
                          </Typography>
                          {product.description && (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                display: "-webkit-box",
                                WebkitLineClamp: isExpanded ? "unset" : 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {product.description}
                            </Typography>
                          )}
                          {product.description && product.description.length > 60 && (
                            <Typography
                              component="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDescription(key);
                              }}
                              sx={{
                                alignSelf: "flex-start",
                                border: "none",
                                background: "none",
                                p: 0,
                                font: "inherit",
                                fontSize: "0.75rem",
                                color: "primary.main",
                                cursor: "pointer",
                              }}
                            >
                              {isExpanded ? "See Less" : "See More"}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "text.primary", mt: "auto", pt: 0.5 }}>
                            {/* No separate currency badge — Meta's price field already comes back
                                as "amount + currency" together (e.g. "500.00 PKR"); appending our
                                own currency field on top of it just duplicated/mislabeled it (it
                                defaults to "USD" whenever Meta doesn't return a separate currency,
                                which is effectively always for this field set). */}
                            {product.price}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          )}

          {mode === "sets" && (
            <>
              {setsStatus === "loading" && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {setsStatus !== "loading" && catalogSetsForActive.length === 0 && (
                <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", fontStyle: "italic", py: 2 }}>
                  No saved Catalog Sets for this catalog yet. Select products, then "Save as Set" to create one.
                </Typography>
              )}
              {catalogSetsForActive.map((set) => (
                <Box
                  key={set._id || set.id}
                  onClick={() => handleUseSet(set)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.875,
                    px: 1,
                    mb: 0.5,
                    borderRadius: "8px",
                    cursor: "pointer",
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": { borderColor: theme.palette.primary.main },
                  }}
                >
                  <Box sx={{ display: "flex", flexShrink: 0 }}>
                    {(set.products || []).slice(0, 3).map((p, i) => (
                      <Avatar
                        key={i}
                        src={hasValidImage(p.imageUrl) ? p.imageUrl : undefined}
                        variant="rounded"
                        sx={{ width: 28, height: 28, ml: i > 0 ? -1 : 0, border: `2px solid ${theme.palette.background.paper}`, bgcolor: "action.hover" }}
                      >
                        {!hasValidImage(p.imageUrl) && <Gallery size={12} color={theme.palette.text.secondary} variant="Linear" />}
                      </Avatar>
                    ))}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.primary" }}>
                      {set.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      {(set.products || []).length} product{(set.products || []).length === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => handleEditSet(e, set)} aria-label="Edit set">
                    <Edit2 size={16} color={theme.palette.text.secondary} variant="Linear" />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => handleDeleteSet(e, set._id || set.id)} aria-label="Delete set">
                    <Trash size={16} color={theme.palette.error.main} variant="Linear" />
                  </IconButton>
                </Box>
              ))}
            </>
          )}
        </Box>

        <Divider />

        {/* Footer — save-as-set + send, mirrors the count/confirm bar CatalogSend.jsx uses. */}
        <Box sx={{ px: { xs: 1.25, sm: "20px" }, py: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
          {savingSet ? (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                size="small"
                autoFocus
                placeholder="Set name"
                value={newSetName}
                onChange={(e) => setNewSetName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveAsSet()}
                sx={{ flex: 1 }}
              />
              <Button size="small" variant="contained" onClick={handleSaveAsSet} disabled={!newSetName.trim()}>
                Save
              </Button>
              <Button size="small" onClick={() => setSavingSet(false)}>
                Cancel
              </Button>
            </Box>
          ) : editingSet && mode === "products" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                size="small"
                autoFocus
                placeholder="Set name"
                value={newSetName}
                onChange={(e) => setNewSetName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleUpdateSet()}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button size="small" onClick={handleCancelEditSet}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleUpdateSet}
                  disabled={!newSetName.trim() || selectedProducts.length === 0}
                  startIcon={<TickCircle size={16} color="currentColor" variant="Bold" />}
                  sx={{ textTransform: "none", borderRadius: "8px" }}
                >
                  Update Set {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ""}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              {mode === "products" && selectedProducts.length > 0 && (
                <Button size="small" variant="outlined" onClick={() => setSavingSet(true)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                  Save as Set
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={handleConfirmSend}
                disabled={selectedProducts.length === 0}
                startIcon={<TickCircle size={16} color="currentColor" variant="Bold" />}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                Add to message {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ""}
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InboxCatalogSend;
